import { bff, toClientError } from './bff'
import { BFF_URL } from './config'

const run = (p) => p.then((r) => r.data).catch((e) => Promise.reject(toClientError(e)))

// ---- Knowledge map ----
export const getMindmap = ({ project, includeData }) =>
  run(
    bff.get('/api/research/mindmap', {
      params: { project, include_data: includeData ? 1 : undefined },
    })
  )

// One build batch (up to `limit` not-yet-analyzed docs). No client timeout — a
// batch is LLM-heavy. The caller loops until `done`.
export const buildMindmapBatch = ({ project, limit }) =>
  run(bff.post('/api/research/mindmap/build', { project, limit }, { timeout: 0 }))

export const analyzeTopics = ({ project, topics, kinds }) =>
  run(bff.post('/api/research/topics', { project, topics, kinds }, { timeout: 0 }))

export const listTopicAnalyses = ({ project, limit }) =>
  run(bff.get('/api/research/topics/history', { params: { project, limit } }))

export const deleteTopicAnalysis = ({ project, id }) =>
  run(bff.delete('/api/research/topics/history', { data: { project, id } }))

// ---- Translation (translate a Research output / arbitrary text) ----
// Available providers: `light` (LLM, no Docker — always on) and `t8r` (the
// backend, only when its LAN/VPN host is wired). Shape mirrors rag strategies.
export const getTranslateProviders = () => run(bff.get('/api/translate/providers'))

// Effective light-translation LLM config (provider + model) + which LLM
// providers have a key, for the Settings picker.
export const getTranslateConfig = () => run(bff.get('/api/translate/config'))

// Large text is routed to the chunked document endpoint (markdown-safe); short
// text uses the single-pass endpoint. The 12k threshold matches translate_text's
// single-pass cap on the engine.
const LIGHT_SINGLE_PASS_MAX = 12000

// One unified endpoint for both providers. Large text goes to the chunked
// /document path, short text to single-pass /text; the BFF routes by `provider`
// (light or t8r) and falls back to light with a notice (fell_back) when t8r is
// off. All write the same project translation history.
export const translateText = ({ provider = 'light', text, source_lang, target_lang, domain, project, save }) => {
  const path = (text || '').length > LIGHT_SINGLE_PASS_MAX ? '/api/translate/document' : '/api/translate/text'
  return run(
    bff.post(
      path,
      { provider, text, source_language: source_lang, target_language: target_lang, domain, project, save },
      { timeout: 0 }
    )
  )
}

export const listTranslations = ({ project, limit }) =>
  run(bff.get('/api/research/translate/history', { params: { project, limit } }))

export const deleteTranslation = ({ project, id }) =>
  run(bff.delete('/api/research/translate/history', { data: { project, id } }))

// Translate an Ask answer and attach the result to its Q&A history entry.
export const translateAnswer = ({ project, qa_id, text, target_lang }) =>
  run(bff.post('/api/research/answer/translate', { project, qa_id, text, target_lang }, { timeout: 0 }))

// ---- Truth lens (per-claim corpus consensus on an answer) ----
export const truthLens = ({ project, qa_id, answer, claims }) =>
  run(bff.post('/api/research/truth-lens', { project, qa_id, answer, claims }, { timeout: 0 }))

// ---- Impact analysis (C2): change notes vs an indexed codebase ----
export const runImpactAnalysis = ({ project, change_text, max_items }) =>
  run(bff.post('/api/research/impact', { project, change_text, max_items }, { timeout: 0 }))
export const listImpactHistory = (limit = 20) =>
  run(bff.get('/api/research/impact/history', { params: { limit } }))
export const getImpactRun = (id) => run(bff.get(`/api/research/impact/history/${id}`))
export const deleteImpactRun = (id) => run(bff.delete(`/api/research/impact/history/${id}`))

// ---- Code graph (Neo4j — ground data for the "Graph RAG" ask strategy) ----
// `project` binds the repo so Graph RAG scopes structural answers to it.
export const buildCodeGraph = ({ repo_path, project }) =>
  run(bff.post('/api/research/code/graph', { repo_path, project }, { timeout: 0 }))

// ---- Code explorer (doc-gen tools — deterministic reads over the reports/graph) ----
export const codeOverview = ({ repo_path, depth, top }) =>
  run(bff.get('/api/research/code/overview', { params: { repo_path, depth, top }, timeout: 0 }))
export const codeModuleGraph = ({ repo_path, depth, cap }) =>
  run(bff.get('/api/research/code/module-graph', { params: { repo_path, depth, cap }, timeout: 0 }))
export const codeApiSurface = ({ repo_path }) =>
  run(bff.get('/api/research/code/api-surface', { params: { repo_path }, timeout: 0 }))
export const codeTrace = ({ repo_path, entry, max_depth }) =>
  run(bff.get('/api/research/code/trace', { params: { repo_path, entry, max_depth }, timeout: 0 }))
export const codeSymbolLookup = ({ repo_path, symbol, what }) =>
  run(bff.get('/api/research/code/symbol', { params: { repo_path, symbol, what }, timeout: 0 }))
// One-click docs: saga → saved Report + MkDocs tree (poll saga; compose step
// carries report_id + mkdocs_dir). `sections` limits which chapters are built
// (omit/empty = all); `data_dictionary` adds the opt-in LLM entity glossary.
export const generateCodeDocs = ({ project, repo_path, sections, mkdocs = true, data_dictionary }) =>
  run(bff.post('/api/research/code/docs', {
    project, repo_path, mkdocs,
    ...(Array.isArray(sections) && sections.length ? { sections } : {}),
    ...(data_dictionary ? { data_dictionary: true } : {}),
  }))

// ---- Per-project repo binding (Graph RAG scoping) ----
export const listCodeRepos = (project) =>
  run(bff.get(`/api/research/code/repos${project ? `?project=${encodeURIComponent(project)}` : ''}`))
export const unbindCodeRepo = ({ project, repo_path }) =>
  run(bff.delete('/api/research/code/repos', { data: { project, repo_path } }))

// ---- Link health (are indexed sources' URLs still alive?) ----
export const checkLinkHealth = ({ project }) =>
  run(bff.post('/api/research/link-health', { project }, { timeout: 0 }))

// ---- Voice ----
export const listVoices = ({ language, voice_type } = {}) =>
  run(bff.get('/api/research/voices', { params: { language, voice_type } }))

export const startNarration = (body) =>
  run(bff.post('/api/research/narrate', body, { timeout: 60000 }))

export const getNarration = (id) => run(bff.get(`/api/research/narrate/${id}`))

// Absolute URL for an <audio> element (served by the BFF, streaming the file).
export const narrationAudioUrl = (id) => `${BFF_URL}/api/research/narrate/${id}/audio`
